/**
 * Helpers for the taxonomy rule model. A rule is a decision table of
 * `branches` (IF-group → THEN); legacy rules stored a single flat AND list.
 * `getRuleBranches` normalizes both into branches so every consumer — the
 * editor, the list display, the version diff — reads one shape.
 */
import { i18n } from '@/shared/i18n';
import type {
  TaxonomyRule,
  TaxonomyRuleActionType,
  TaxonomyRuleBranch,
  TaxonomyRuleConditionGroup,
} from '../model/types';

/** An empty AND group (the starting point for a new branch's condition tree). */
export function emptyConditionGroup(): TaxonomyRuleConditionGroup {
  return { combinator: 'and', conditions: [], groups: [] };
}

/**
 * Coerces a stored group into the shape the editors assume.
 *
 * `groups` is optional on the wire — the API's tree constraint treats a group
 * without the key as a leaf — and rules are copied verbatim into every new
 * version, so a branch authored outside this UI keeps that shape forever.
 * Normalizing here rather than at each reader is what stops the rule list, the
 * edit modal and the version diff from dereferencing an array that isn't there:
 * they all read through `getRuleBranches`, and a throw on the render path takes
 * out the dashboard shell, not just the Rules tab.
 */
function normalizeConditionGroup(
  group: TaxonomyRuleConditionGroup | undefined,
): TaxonomyRuleConditionGroup {
  const conditions = group?.conditions;
  const groups = group?.groups;
  return {
    combinator: group?.combinator ?? 'and',
    conditions: Array.isArray(conditions) ? conditions : [],
    groups: Array.isArray(groups) ? groups.map(normalizeConditionGroup) : [],
  };
}

/** Normalizes any rule (legacy-flat or multi-branch) into its list of branches. */
export function getRuleBranches(rule: TaxonomyRule): TaxonomyRuleBranch[] {
  if (rule.branches?.length) {
    return rule.branches.map((b) => ({ ...b, when: normalizeConditionGroup(b.when) }));
  }
  /* eslint-disable @typescript-eslint/no-deprecated --
   * Reading the deprecated flat shape IS this function: it is the shim that
   * lets rules saved before `branches` existed render alongside new ones.
   */
  const action: TaxonomyRuleActionType = rule.action ?? 'require_field';
  return [
    {
      id: rule.id,
      when: { combinator: 'and', conditions: rule.conditions ?? [], groups: [] },
      action,
      targetFieldId: rule.targetFieldId ?? '',
      values: rule.values,
      defaultValue: rule.defaultValue,
    },
  ];
  /* eslint-enable @typescript-eslint/no-deprecated */
}

/** Total number of leaf conditions across a group and all its descendants. */
export function countConditions(group: TaxonomyRuleConditionGroup): number {
  return group.conditions.length + group.groups.reduce((n, g) => n + countConditions(g), 0);
}

// Getters, not values: a resolved label here would freeze at import.
const OPERATOR_LABEL: Record<string, string> = {
  get is() {
    return i18n.t('knowledge:taxonomy.rules.is');
  },
  get is_not() {
    return i18n.t('knowledge:taxonomy.rules.isNot');
  },
};

/**
 * Renders a condition group to a readable boolean expression, e.g.
 * `Region is “Regional” AND (Country is “US” OR Country is “CA”)`. Nested
 * groups are parenthesized; the top-level call omits the outer parens.
 */
export function describeConditionGroup(
  group: TaxonomyRuleConditionGroup,
  nameOf: (id: string) => string,
  depth = 0,
): string {
  const parts: string[] = [
    ...group.conditions.map(
      (c) => `${nameOf(c.fieldId)} ${OPERATOR_LABEL[c.operator] ?? OPERATOR_LABEL.is} “${c.value}”`,
    ),
    ...group.groups.map((g) => describeConditionGroup(g, nameOf, depth + 1)),
  ];
  if (parts.length === 0) return i18n.t('knowledge:taxonomy.rules.noConditions');
  const joined = parts.join(group.combinator === 'or' ? ' OR ' : ' AND ');
  return depth > 0 && parts.length > 1 ? `(${joined})` : joined;
}

/** Human summary of one branch's THEN action. */
export function describeBranchAction(
  branch: TaxonomyRuleBranch,
  nameOf: (id: string) => string,
): string {
  const target = nameOf(branch.targetFieldId);
  switch (branch.action) {
    case 'limit_options':
      return `Limit ${target} options to ${(branch.values ?? []).join(', ') || '—'}`;
    case 'show_field':
      return `Show ${target}`;
    case 'set_default':
      return `Set ${target} default to ${branch.defaultValue || '—'}`;
    case 'require_field':
      return `Require ${target}`;
    default:
      return target;
  }
}
